import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, ChevronLeft } from "lucide-react";

export const StoryHighlights = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    fetch("/brand/social/manifest.json").then((r) => (r.ok ? r.json() : [])).then(setItems).catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (open === null) return;
    const t = setTimeout(() => setOpen((i) => (i + 1 < items.length ? i + 1 : null)), 7000);
    const key = (e) => { if (e.key === "Escape") setOpen(null); };
    window.addEventListener("keydown", key);
    return () => { clearTimeout(t); window.removeEventListener("keydown", key); };
  }, [open, items.length]);

  if (!items.length) return null;
  const cur = open !== null ? items[open] : null;

  return (
    <section id="highlights" className="py-16 px-6 max-w-7xl mx-auto" data-testid="story-highlights-section">
      <div className="text-center mb-10">
        <span className="text-xs tracking-widest text-primary font-bold">تعرّف علينا في دقيقة</span>
        <h2 className="text-2xl sm:text-3xl font-bold font-heading mt-2">قصة رواج الذكية</h2>
        <p className="text-muted-foreground text-sm mt-2 max-w-xl mx-auto">اضغط على أي دائرة لتشاهد البطاقة — منصة سعودية تولّد هويتك ومحتواك وأصواتك بطابع سعودي ١٠٠٪ بلمسة زر.</p>
      </div>

      <div className="flex justify-center gap-5 sm:gap-10 flex-wrap">
        {items.map((it, i) => (
          <motion.button key={it.key} onClick={() => setOpen(i)} whileHover={{ scale: 1.08, y: -4 }} whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
            className="group flex flex-col items-center gap-3 w-24 sm:w-32" data-testid={`highlight-${it.key}`}>
            <span className="relative p-[3px] rounded-full bg-gradient-to-tr from-primary via-accent to-primary group-hover:gold-glow transition-shadow">
              <span className="block p-[3px] rounded-full bg-background">
                <img src={it.highlight} alt={it.title} loading="lazy" className="w-20 h-20 sm:w-28 sm:h-28 rounded-full object-cover" />
              </span>
            </span>
            <span className="text-sm font-bold">{it.title}</span>
          </motion.button>
        ))}
      </div>

      <div className="mt-12 hidden lg:grid grid-cols-5 gap-5">
        {items.map((it, i) => (
          <motion.button key={it.key} onClick={() => setOpen(i)} whileHover={{ y: -8 }} className="rounded-2xl overflow-hidden ring-1 ring-border hover:ring-primary/60 transition shadow-xl shadow-black/40" data-testid={`story-card-${it.key}`}>
            <img src={it.story} alt={it.title} loading="lazy" className="w-full aspect-[9/16] object-cover" />
          </motion.button>
        ))}
      </div>

      <AnimatePresence>
        {cur && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(null)}
            className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" data-testid="story-viewer">
            <button className="absolute top-4 left-4 text-white/80 hover:text-white" onClick={() => setOpen(null)} data-testid="story-viewer-close"><X className="w-7 h-7" /></button>
            <div className="absolute top-4 inset-x-16 flex gap-1.5" dir="rtl">
              {items.map((_, i) => (
                <span key={i} className="h-1 flex-1 rounded-full bg-white/25 overflow-hidden">
                  <span className={`block h-full bg-primary ${i < open ? "w-full" : i === open ? "animate-[grow_7s_linear_forwards]" : "w-0"}`} />
                </span>
              ))}
            </div>
            <motion.img key={cur.key} src={cur.story} alt={cur.title} onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.3 }}
              className="max-h-[88vh] aspect-[9/16] rounded-2xl shadow-2xl object-cover" data-testid="story-viewer-image" />
            {open > 0 && <button className="absolute right-4 sm:right-10 text-white/70 hover:text-white" onClick={(e) => { e.stopPropagation(); setOpen(open - 1); }} data-testid="story-prev"><ChevronRight className="w-10 h-10" /></button>}
            {open < items.length - 1 && <button className="absolute left-4 sm:left-10 text-white/70 hover:text-white" onClick={(e) => { e.stopPropagation(); setOpen(open + 1); }} data-testid="story-next"><ChevronLeft className="w-10 h-10" /></button>}
          </motion.div>
        )}
      </AnimatePresence>
      <style>{`@keyframes grow{from{width:0}to{width:100%}}`}</style>
    </section>
  );
};
